import { shallowEqual } from "react-redux";

// Spark components
import { GuxButton, GuxIcon, GuxTooltip } from "genesys-spark-components-react";

// Utils
import { toGuxDisabled } from "util/componentUtil";

// Common
import { DefaultLimits } from "constants/DefaultLimits";
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Feature types
import { TemplateQuestion } from "features/qualityForm/qualityFormSlices/qualityFormTypes";

// Feature slice
import { qualityFormActions } from "features/qualityForm/qualityFormSlice";
import {
    getMultipleSelectOptionQuestion,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Validations
import {
    hasMinimumAnswerOptions,
    hasMaximumAnswerOptions,
} from "features/qualityForm/qualityFormValidations/qualityFormAnswerValidations";

// Question common
import { PropertySection } from "../common/PropertySection";
import { MultipleSelectAnswerOption } from "./MultipleSelectAnswerOption";

// Strings
import strings from "./Question.strings";

export default function MultipleSelectAnswerOptionsSection({
    questionId,
}: {
    questionId: string;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);

    // Not checking any multiple selection FT here because it should be blocked
    // at QuestionEditor level if FT is off

    const { multipleSelectOptionQuestions } = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        return {
            multipleSelectOptionQuestions:
                question?.multipleSelectOptionQuestions,
        };
    }, shallowEqual);

    const hasEnoughAnswerOptions = useAppSelector((state) =>
        hasMinimumAnswerOptions(state, questionId, true)
    );

    const hasTooManyAnswerOptions = useAppSelector((state) =>
        hasMaximumAnswerOptions(state, questionId, true)
    );

    return (
        <PropertySection title={t.labels.multipleSelect.answerOptions}>
            {multipleSelectOptionQuestions?.map(
                (multipleSelectOptionId, index) => {
                    return (
                        <MultipleSelectAnswerOption
                            questionId={questionId}
                            multipleSelectOptionId={multipleSelectOptionId}
                            canDeleteAnswer={hasEnoughAnswerOptions}
                            answerIndex={index}
                            key={multipleSelectOptionId}
                        />
                    );
                }
            )}
            <div
                className="add-answer-button-group"
                data-testid="add-answer-button-group"
            >
                <GuxButton
                    className="add-answer"
                    data-testid="add-answer-button"
                    disabled={toGuxDisabled(hasTooManyAnswerOptions)}
                    aria-disabled={toGuxDisabled(hasTooManyAnswerOptions)}
                    onClick={() => {
                        dispatch(
                            qualityFormActions.addMultipleSelectOption({
                                questionId: questionId,
                                template:
                                    getMultipleSelectOptionQuestion() as TemplateQuestion,
                            })
                        );
                    }}
                >
                    <GuxIcon icon-name="add" decorative />
                    <span
                        className={`add-answer-label${
                            hasTooManyAnswerOptions ? "-disabled" : ""
                        }`}
                    >
                        {t.labels.multipleSelect.addAnswer}
                    </span>
                </GuxButton>
                {hasTooManyAnswerOptions && (
                    <GuxTooltip>
                        <div slot="content">
                            {hasTooManyAnswerOptions &&
                                t.labels.multipleSelectAnswerLimitReached({
                                    maxAnswerOptionCount:
                                        DefaultLimits.maxAnswerOptionCount,
                                })}
                        </div>
                    </GuxTooltip>
                )}
            </div>
        </PropertySection>
    );
}
